import { useEffect, RefObject } from 'react'
import { getFocusModality } from '@/utils/focusModality'

/**
 * Tracks whether focus entered a wrapper element via mouse click or keyboard
 * tab and sets a `data-focus-method` attribute ("mouse" | "keyboard") on the
 * element.  CSS in main.css uses this attribute to differentiate visual focus
 * treatments.
 *
 * Text inputs always receive `:focus-visible` even on mouse click (browsers
 * expect keyboard interaction to follow), so pure-CSS `:focus-visible` vs
 * `:focus` differentiation does not work for wrapped input components.  This
 * hook solves that by reading the global focus modality (see focusModality.ts)
 * on `focusin`.
 *
 * The method is determined when focus **enters** the wrapper from outside.
 * Internal focus moves (e.g. arrowing between DateField segments) preserve the
 * original method so the ring style doesn't flash mid-interaction.
 */
export function useFocusMethod (ref: RefObject<HTMLElement | null>): void {
	useEffect(() => {
		const el = ref.current
		if (!el) return

		const onFocusIn = (e: FocusEvent) => {
			const from = e.relatedTarget as Node | null
			// Internal focus move (e.g. arrow between segments) — keep current method
			if (from && el.contains(from)) return
			el.setAttribute(
				'data-focus-method',
				getFocusModality() === 'pointer' ? 'mouse' : 'keyboard'
			)
		}

		const onFocusOut = (e: FocusEvent) => {
			// Only clear when focus leaves the wrapper entirely
			if (!el.contains(e.relatedTarget as Node)) {
				el.removeAttribute('data-focus-method')
			}
		}

		el.addEventListener('focusin', onFocusIn)
		el.addEventListener('focusout', onFocusOut)

		return () => {
			el.removeEventListener('focusin', onFocusIn)
			el.removeEventListener('focusout', onFocusOut)
		}
	}, [ref])
}
